import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Drawer } from "@material-ui/core";

import Swal from "sweetalert2";
import { Profile, SidebarNav } from "./components";
import { isDev, listOfSubscriptions } from "../../../../common/envConfig";
import { userService } from "../../../../services/user";
import { useUserData } from "../../../../hooks/useUserData";
import { useAuthUser } from "../../../../hooks/useAuthUser";
import { useUserSubscriptionAddons } from "../../../../hooks/useUserSubscriptionAddons";

import { FiBarChart2, FiBell, FiFolder, FiUsers } from "react-icons/fi";
import { IoMdBusiness } from "react-icons/io";
import {
  MdAssignment,
  MdAssistant,
  MdComputer,
  MdOutlineDevices,
  MdOutlineSports,
  MdPlayCircleFilled,
  MdSearch,
  MdToc,
} from "react-icons/md";
import { FaBalanceScaleLeft } from "react-icons/fa";
import { LuBuilding2 } from "react-icons/lu";
import { RiBarChartFill, RiHome6Line, RiSettingsLine } from "react-icons/ri";
import { SlGraph } from "react-icons/sl";
import { RiUserSearchLine } from "react-icons/ri";
import { useAllSwingCollectionsByUserId } from "../../../../views/swingCollections/hooks/useAllSwingCollectionsByUserId";

function Sidebar({ open, variant, onClose, className, history, ...rest }) {
  const { collections } = useAllSwingCollectionsByUserId();
  const { user } = useAuthUser();
  const { userData } = useUserData();
  const { llmAddon } = useUserSubscriptionAddons();

  const [isStaffEnterprise, setIsStaffEnterprise] = useState(true);

  useEffect(() => {
    if (!userData || !user) return;

    const processUserData = async () => {
      try {
        if (
          userData.subscriptionType?.includes("enterprise_master") &&
          userData.enterpriseAccountId
        ) {
          const enterpriseData = await userService.verifyStaffEnt(user.uid);
          setIsStaffEnterprise(Boolean(enterpriseData?.isStaffEnterprise));
        } else {
          setIsStaffEnterprise(true);
        }
      } catch {
        setIsStaffEnterprise(true);
      }
    };

    processUserData();
  }, [userData, user]);

  const defaultPages = useMemo(
    () => [
      {
        title: (
          <img src="/images/logos/SportsboxLogo.png" alt="logo" width={"75%"} />
        ),
        href: "/dashboard",
      },
    ],
    [],
  );

  const collectionsNode = useMemo(() => {
    const canSeeCollections =
      (userData?.role === "admin" || llmAddon) && collections?.length !== 0;
    if (!canSeeCollections) {
      return null;
    }

    const children = collections?.map((c) => ({
      title: c.collectionName || "Untitled",
      href: `/collections/${encodeURIComponent(c.id)}`,
      icon: FiFolder,
    }));

    return {
      title: "Collections",
      icon: FiFolder,
      href: "/collections",
      children,
    };
  }, [collections, userData?.role, llmAddon]);

  const swingLibraryNode = useMemo(
    () => ({
      title: "Swing Library",
      href: "/swing-library",
      icon: FiBarChart2,
      children: collectionsNode ? [collectionsNode] : undefined,
    }),
    [collectionsNode],
  );

  const devPages = useMemo(() => [], []);

  const basePages = useMemo(
    () => [
      swingLibraryNode,
      ...(isDev ? devPages : []),
      { title: "Compare", href: "/compare", icon: FaBalanceScaleLeft },
      { title: "Dashboard", href: "/dashboard", icon: RiHome6Line },
      { title: "Account", href: "/account", icon: RiSettingsLine },
      {
        title: "Remote Session",
        href: "/remote-session",
        icon: MdOutlineDevices,
      },
      { title: "Coaches", href: "/coaches", icon: LuBuilding2 },
      { title: "Studio Coaches", href: "/studio-coaches", icon: LuBuilding2 },
      { title: "Students", href: "/students", icon: FiUsers },
      { title: "Invites", href: "/invites", icon: FiBell },
      { title: "Search", href: "/search", icon: MdSearch },
      { title: "Admin Search", href: "/admin-search", icon: RiUserSearchLine },
      { title: "Summary Graphs", href: "/summary-graphs", icon: SlGraph },
      {
        title: "Weekly User Reports",
        href: "/weekly-reports",
        icon: MdAssignment,
      },
      { title: "Practice Guides", href: "/practice-guides", icon: MdToc },
      { title: "Enterprise", href: "/enterprise", icon: IoMdBusiness },
      { title: "Studio", href: "/studio", icon: MdComputer },
      { title: "Unity", href: "/unity", icon: MdPlayCircleFilled },
    ],
    [swingLibraryNode, isDev],
  );

  const pages = useMemo(() => {
    if (userData?.role === "admin") {
      return [...defaultPages, ...basePages];
    }

    let temp = [...basePages];

    switch (userData?.subscriptionType) {
      case listOfSubscriptions.STUDENT_FREE:
      case listOfSubscriptions.STUDENT_LITE_ANNUALLY:
      case listOfSubscriptions.STUDENT_LITE_MONTHLY:
        temp = temp.filter((i) =>
          ["Account", "Dashboard", "Search", "Invites"].includes(i.title),
        );
        break;

      case listOfSubscriptions.PLAYER_PLUS_ANNUALLY:
      case listOfSubscriptions.PLAYER_PLUS_MONTHLY:
        temp = temp.filter((i) =>
          [
            "Swing Library",
            ...(llmAddon ? ["Compare"] : []),
            "Account",
            "Dashboard",
            "Search",
            "Invites",
          ].includes(i.title),
        );
        break;

      case listOfSubscriptions.LITE_MONTHLY:
      case listOfSubscriptions.LITE_ANNUALLY:
      case listOfSubscriptions.PREMIUM_MONTHLY:
      case listOfSubscriptions.PREMIUM_ANNUALLY:
      case listOfSubscriptions.ENTERPRISE_COACH:
      case listOfSubscriptions.STUDIO_COACH:
        temp = temp.filter((i) =>
          [
            "Swing Library",
            ...(llmAddon ? ["Compare"] : []),
            "Account",
            "Dashboard",
            "Search",
            "Students",
          ].includes(i.title),
        );
        break;

      case listOfSubscriptions.ENTERPRISE_MASTER:
        if (isStaffEnterprise) {
          temp = temp.filter((i) =>
            [
              "Swing Library",
              ...(llmAddon ? ["Compare"] : []),
              "Account",
              "Dashboard",
              "Search",
              "Students",
            ].includes(i.title),
          );
        } else {
          temp = temp.filter((i) =>
            [
              "Swing Library",
              ...(llmAddon ? ["Compare"] : []),
              "Account",
              "Dashboard",
              "Search",
              "Students",
              "Coaches",
            ].includes(i.title),
          );
        }
        break;

      case listOfSubscriptions.STUDIO_MASTER:
        temp = temp.filter((i) =>
          [
            "Swing Library",
            ...(llmAddon ? ["Compare"] : []),
            "Account",
            "Dashboard",
            "Search",
            "Students",
            "Studio Coaches",
          ].includes(i.title),
        );
        break;

      default:
        temp = temp.filter((i) =>
          ["Account", "Dashboard", "Search", "Invites"].includes(i.title),
        );
        break;
    }

    return [...defaultPages, ...temp];
  }, [
    userData?.role,
    userData?.subscriptionType,
    isStaffEnterprise,
    llmAddon,
    defaultPages,
    basePages,
    onClose,
  ]);

  return (
    <Drawer
      anchor="left"
      onClose={onClose}
      open={open}
      variant={variant}
      id="sidebarPage"
      onClick={Swal.close()}
      PaperProps={{ style: { width: 260, height: "100%" } }}
    >
      <div
        {...rest}
        className={clsx("flex flex-col h-full p-4 bg-white", className)}
      >
        <SidebarNav className="mb-4 flex-1" pages={pages} onClose={onClose} />
        <Profile history={history} />
      </div>
    </Drawer>
  );
}

Sidebar.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
  variant: PropTypes.string.isRequired,
  history: PropTypes.object,
};

export default Sidebar;
